import { toast } from "sonner";
import { useState, useContext, useEffect } from "react";
import { CrmProvider, CrmFormData, AuthMethod } from "./types";
import CrmSelector from "./CrmSelector";
import PipelineCrmConfiguration from "./PipelineCrmConfiguration";
import AuthCrmConfiguration from "./AuthCrmConfiguration";
import GlobalVariablesConfiguration from "./smarthome/GlobalVariablesConfiguration";
import SoftinmCrmConfiguration from "./softinm/SoftinmCrmConfiguration";
import SoftinmGlobalVariablesConfiguration from "./softinm/SoftinmGlobalVariablesConfiguration";
import DomusCrmConfiguration from "./domus/DomusCrmConfiguration";
import DomusGlobalVariablesConfiguration from "./domus/DomusGlobalVariablesConfiguration";
import SalesforceCrmConfiguration from "./salesforce/SalesforceCrmConfiguration";
import BitrixCrmConfiguration from "./bitrix/BitrixCrmConfiguration";
import SimiGlobalVariablesConfiguration from "./simi/SimiGlobalVariablesConfiguration";
import SimiCrmConfiguration from "./simi/SimiCrmConfiguration";
import HubspotCrmConfiguration from "./hubspot/HubspotCrmConfiguration";
import HubspotGlobalVariablesConfiguration from "./hubspot/HubspotGlobalVariablesConfiguration";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const CRM_PROVIDERS: Array<{
  id: CrmProvider;
  name: string;
  supportedAuthMethods: AuthMethod[];
}> = [
  {
    id: "pipedrive",
    name: "Pipedrive",
    supportedAuthMethods: ["apiKey"],
  },
  {
    id: "smarthome-rentals",
    name: "SmartHome Rentals",
    supportedAuthMethods: ["companyCodeProjectCode"],
  },
  {
    id: "softinm",
    name: "Softinm",
    supportedAuthMethods: ["apiKey"],
  },
  {
    id: "domus",
    name: "Domus",
    supportedAuthMethods: ["apiKey"],
  },
  {
    id: "salesforce",
    name: "Salesforce",
    supportedAuthMethods: ["oauth"],
  },
  {
    id: "bitrix",
    name: "Bitrix24",
    supportedAuthMethods: ["urlMethodTokens"],
  },
  {
    id: "simi",
    name: "Simi",
    supportedAuthMethods: ["oauth"],
  },
  {
    id: "hubspot",
    name: "HubSpot",
    supportedAuthMethods: ["apiKey"],
  },
  // Future providers can be added here
  // {
  //   id: "zoho",
  //   name: "Zoho",
  //   supportedAuthMethods: ["oauth", "apiKey"],
  // },
];

const CRM_OPTIONS = CRM_PROVIDERS.map((p) => ({
  id: p.id,
  name: p.name,
}));

const CrmConfiguration = (): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const [formData, setFormData] = useState<CrmFormData>({
    provider: null,
    authMethod: null,
    apiKey: "",
    companyCode: "",
    projectCode: "",
    companyName: "",
    instanceUrl: "",
    clientId: "",
    clientSecret: "",
    bitrixBaseUrl: "",
    bitrixCreateContactToken: "",
    bitrixCreateLeadToken: "",
    bitrixUpdateLeadToken: "",
    bitrixCreateDealToken: "",
    bitrixUpdateDealToken: "",
    bitrixCreateContactEnabled: true,
    bitrixCreateLeadEnabled: true,
    bitrixUpdateLeadEnabled: true,
    bitrixCreateDealEnabled: true,
    bitrixUpdateDealEnabled: true,
    scope: "",
    inmobiliaria: "",
    hubspotAccessToken: "",
  });

  const { data: crmConnection, isLoading: isLoadingConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const selectedProvider = CRM_PROVIDERS.find(
    (p) => p.id === (crmConnection?.provider || formData.provider)
  );

  // Initialize form data when connection exists with error status
  useEffect(() => {
    if (crmConnection && crmConnection.status === "error") {
      const provider = CRM_PROVIDERS.find((p) => p.id === crmConnection.provider);
      setFormData({
        provider: crmConnection.provider,
        authMethod: (provider?.supportedAuthMethods[0] as AuthMethod | undefined) || null,
        apiKey:
          crmConnection.pipedrive?.apiKey ||
          crmConnection.softinm?.apiKey ||
          crmConnection.domus?.apiKey ||
          "",
        companyCode: crmConnection.smarthomeRentals?.companyCode || "",
        projectCode: crmConnection.smarthomeRentals?.projectCode || "",
        companyName: crmConnection.softinm?.companyName || "",
        instanceUrl: crmConnection.salesforce?.instanceUrl || "",
        clientId: crmConnection.salesforce?.clientId || (crmConnection as any).simi?.clientId || "",
        clientSecret: crmConnection.salesforce?.clientSecret || (crmConnection as any).simi?.clientSecret || "",
        bitrixBaseUrl: crmConnection.bitrix?.baseUrl || "",
        bitrixCreateContactToken: crmConnection.bitrix?.methods?.createContact?.token || "",
        bitrixCreateLeadToken: crmConnection.bitrix?.methods?.createLead?.token || "",
        bitrixUpdateLeadToken: crmConnection.bitrix?.methods?.updateLead?.token || "",
        bitrixCreateDealToken: crmConnection.bitrix?.methods?.createDeal?.token || "",
        bitrixUpdateDealToken: crmConnection.bitrix?.methods?.updateDeal?.token || "",
        bitrixCreateContactEnabled:
          crmConnection.bitrix?.methods?.createContact?.enabled ?? true,
        bitrixCreateLeadEnabled: crmConnection.bitrix?.methods?.createLead?.enabled ?? true,
        bitrixUpdateLeadEnabled: crmConnection.bitrix?.methods?.updateLead?.enabled ?? true,
        bitrixCreateDealEnabled: crmConnection.bitrix?.methods?.createDeal?.enabled ?? true,
        bitrixUpdateDealEnabled: crmConnection.bitrix?.methods?.updateDeal?.enabled ?? true,
        scope: (crmConnection as any).simi?.scope || "",
        inmobiliaria: (crmConnection as any).simi?.inmobiliaria?.toString() || "",
        hubspotAccessToken: (crmConnection as any).hubspot?.accessToken || "",
      });
    }
  }, [
    crmConnection?.status,
    crmConnection?.provider,
    crmConnection?.pipedrive?.apiKey,
    crmConnection?.smarthomeRentals?.companyCode,
    crmConnection?.smarthomeRentals?.projectCode,
    crmConnection?.softinm?.apiKey,
    crmConnection?.softinm?.companyName,
    crmConnection?.domus?.apiKey,
    crmConnection?.salesforce?.instanceUrl,
    crmConnection?.salesforce?.clientId,
    crmConnection?.salesforce?.clientSecret,
  ]);

  const utils = trpc.useUtils();
  const setEnabled = trpc.crmSelfConfig.setEnabled.useMutation({
    onSuccess: async (data) => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Estado actualizado", {
        description: data.enabled ? "El CRM ha sido habilitado" : "El CRM ha sido deshabilitado",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar el estado.",
      });
    },
  });

  const handleToggleEnabled = () => {
    if (!crmConnection || !merchantId) return;
    setEnabled.mutate({
      merchantId,
      id: crmConnection.id,
      enabled: !crmConnection.enabled,
    });
  };

  const handleProviderChange = (provider: CrmProvider | null) => {
    setFormData({
      provider,
      authMethod: null,
      apiKey: "",
      companyCode: "",
      projectCode: "",
      companyName: "",
      instanceUrl: "",
      clientId: "",
      clientSecret: "",
      bitrixBaseUrl: "",
      bitrixCreateContactToken: "",
      bitrixCreateLeadToken: "",
      bitrixUpdateLeadToken: "",
      bitrixCreateDealToken: "",
      bitrixUpdateDealToken: "",
      bitrixCreateContactEnabled: true,
      bitrixCreateLeadEnabled: true,
      bitrixUpdateLeadEnabled: true,
      bitrixCreateDealEnabled: true,
      bitrixUpdateDealEnabled: true,
      scope: "",
      inmobiliaria: "",
      hubspotAccessToken: "",
    });
  };

  const handleFormDataChange = (data: Partial<CrmFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  if (isLoadingConnection) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">
            Configuración CRM
          </h2>
          <p className="text-sm text-gray-600">
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  const hasConnection = !!crmConnection;

  return (
    <div className="flex flex-col gap-6">
      {/* Title and Subtitle */}
      <div>
        <h2 className="text-2xl font-bold mb-2">
          Configuración CRM
        </h2>
        <p className="text-sm text-gray-600">
          {hasConnection ? "Gestiona tu conexión CRM" : "Conecta tu CRM en pocos pasos"}
        </p>
      </div>

      {/* CRM Selector - Only show if no connection exists */}
      {!hasConnection && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-3 block">
              Selecciona CRM
            </Label>
            <CrmSelector
              options={CRM_OPTIONS}
              selectedOption={formData.provider}
              onSelect={handleProviderChange}
              placeholder="Selecciona un CRM"
            />
          </div>
        </div>
      )}

      {/* Show selected CRM when connection exists */}
      {hasConnection && crmConnection.provider && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-col gap-4">
            <div>
              <Label className="text-lg font-bold text-gray-700 mb-3 block">
                CRM Seleccionado
              </Label>
              <p className="text-base font-medium text-gray-700 mb-2">
                {selectedProvider?.name || crmConnection.provider}
              </p>
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={crmConnection.enabled ?? false}
                  onClick={handleToggleEnabled}
                  disabled={setEnabled.isLoading}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    (crmConnection.enabled ?? false) ? "bg-palomma-primary" : "bg-gray-300"
                  )}
                >
                  <span
                    className={cn(
                      "pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform",
                      (crmConnection.enabled ?? false) ? "translate-x-4" : "translate-x-0"
                    )}
                  />
                </button>
                <p className={cn("text-sm", crmConnection.enabled ? "text-green-600" : "text-gray-500")}>
                  {crmConnection.enabled ? "Activo" : "Inactivo"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Method and Fields */}
      {selectedProvider && (
        <AuthCrmConfiguration formData={formData} onFormDataChange={handleFormDataChange} />
      )}

      {/* Global Variables Configuration - Only for SmartHome after successful connection */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "smarthome-rentals" && (
          <GlobalVariablesConfiguration crmConnection={crmConnection} />
        )}

      {/* Global Variables Configuration - Only for Domus after successful connection */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "domus" && (
          <DomusGlobalVariablesConfiguration crmConnection={crmConnection} />
        )}

      {/* Global Variables Configuration - Only for Softinm after successful connection */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "softinm" && (
          <SoftinmGlobalVariablesConfiguration crmConnection={crmConnection} />
        )}

      {/* Softinm specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "softinm" && <SoftinmCrmConfiguration />}

      {/* Domus specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "domus" && <DomusCrmConfiguration />}

      {/* Salesforce specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "salesforce" && <SalesforceCrmConfiguration />}

      {/* Bitrix specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "bitrix" && <BitrixCrmConfiguration />}

      {/* Simi specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "simi" && (
          <SimiGlobalVariablesConfiguration crmConnection={crmConnection} />
        )}

      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "simi" && <SimiCrmConfiguration />}

      {/* HubSpot connection-level defaults after successful connection */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "hubspot" && (
          <HubspotGlobalVariablesConfiguration crmConnection={crmConnection} />
        )}

      {/* HubSpot specific configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider === "hubspot" && <HubspotCrmConfiguration />}

      {/* Default CRM Configuration */}
      {hasConnection &&
        crmConnection.status === "success" &&
        crmConnection.provider !== "softinm" &&
        crmConnection.provider !== "domus" &&
        crmConnection.provider !== "salesforce" &&
        crmConnection.provider !== "bitrix" &&
        crmConnection.provider !== "simi" &&
        crmConnection.provider !== "hubspot" && <PipelineCrmConfiguration />}
    </div>
  );
};

export default CrmConfiguration;
